/** Presentational formatters. Pure, locale-aware, no side effects. */

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const relativeFormatter = new Intl.RelativeTimeFormat('en-US', {
  numeric: 'auto',
});

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

/** Format minor-unit cents as a currency string. */
export function formatCurrency(cents: number): string {
  return currencyFormatter.format(cents / 100);
}

const thousandsFormatter = new Intl.NumberFormat('ru-RU');

/**
 * Format a whole number with thousands separators (e.g. `25 000`). Mirrors
 * cpanel `thousandDivider`; the unit suffix (e.g. "сум") is appended by the
 * caller via i18n. Returns an empty string for nullish/NaN input.
 */
export function formatThousands(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '';
  return thousandsFormatter.format(value);
}

/**
 * Group a whole number with ASCII COMMAS (`1,001`), rounding to a whole unit.
 *
 * This is the legacy merchant panel's `thousandDivider`, kept for the
 * toggleable legacy-mode screens, which are compared pixel-for-pixel against
 * that panel. It is deliberately NOT `formatThousands`: that one groups with
 * `ru-RU` thin spaces (`1 001`), which is right for the new UI and wrong here.
 * New screens should use `formatThousands`.
 *
 * Quirks preserved from the original: it rounds rather than truncates, and any
 * non-numeric input collapses to `0` instead of throwing.
 */
export function formatThousandsComma(value: number | string | null | undefined): string {
  const n = Number(value);
  const whole = Number.isFinite(n) ? Math.round(n) : 0;
  return whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Format an ISO timestamp as a readable absolute date-time. */
export function formatDateTime(iso: string): string {
  return dateTimeFormatter.format(new Date(iso));
}

/** Format an ISO timestamp relative to now (e.g. "5 minutes ago"). */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const diffMinutes = Math.round(
    (new Date(iso).getTime() - now.getTime()) / MS_PER_MINUTE,
  );
  const absMinutes = Math.abs(diffMinutes);

  if (absMinutes < MINUTES_PER_HOUR) {
    return relativeFormatter.format(diffMinutes, 'minute');
  }
  const diffHours = Math.round(diffMinutes / MINUTES_PER_HOUR);
  if (Math.abs(diffHours) < HOURS_PER_DAY) {
    return relativeFormatter.format(diffHours, 'hour');
  }
  const diffDays = Math.round(diffHours / HOURS_PER_DAY);
  return relativeFormatter.format(diffDays, 'day');
}
